import { isBefore } from "date-fns";
import { isEmpty } from "lodash";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { DietVariant } from "@/features/diets/types/diet-variant.type";
import { useFormResolver } from "@/lib/form-resolver";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import {
  dropUnavailableDates,
  haveSaturdays,
  haveSundays,
} from "../utils/dates";
import { getVariantFromMealsAndCalories } from "../utils/variants";

const FormSchema = ({
  min = 1,
  max = 99,
  minMeals = 1,
}: {
  min?: number;
  max?: number;
  minMeals?: number;
}) =>
  z.object({
    dates: z
      .array(z.date().or(z.string()).pipe(z.coerce.date()))
      .min(min, { message: "cart.configuration.errors.minDays" })
      .max(max, { message: "cart.configuration.errors.maxDays" }),
    kcal: z
      .string()
      .min(1, { message: "cart.configuration.errors.caloricityRequired" })
      .or(z.number())
      .pipe(z.coerce.string()),
    meals: z
      .array(z.string())
      .min(minMeals, { message: "cart.configuration.errors.minMeals" }),
    daysToSelect: z.number(),
    saturdays: z.boolean(),
    sundays: z.boolean(),
    variant: z.string().default(""),
  });

// nested validation
export const RefinedSchema = ({
  min,
  max,
  minMeals,
  lastPossibleOrderDay,
}: {
  min?: number;
  max?: number;
  minMeals?: number;
  lastPossibleOrderDay?: Date | null;
}) =>
  FormSchema({ min, max, minMeals }).superRefine((val, ctx) => {
    if (val.dates.length !== val.daysToSelect) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cart.configuration.errors.amountOfDays",
        path: ["dates"],
      });
    }

    if (
      lastPossibleOrderDay &&
      val.dates.some((date) => !isBefore(date, lastPossibleOrderDay))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cart.configuration.errors.daysAllowedToOrderInFuture",
        path: ["dates"],
      });
    }
  });

export const useConfigureDietForm = ({
  defaults,
  diet,
  min,
  max,
  minMeals,
  locale,
}: {
  defaults?: {
    mealIds: string[];
    calories: number;
    dates?: string[];
    sourceCalorifics?: number;
    days?: number;
  };
  diet: DietCatalogDetail;
  min?: number;
  max?: number;
  minMeals?: number | null;
  locale: string;
}) => {
  // default values, used when there are no params or defaults
  const defaultConfiguration = {
    saturdays: false,
    sundays: false,
    days: defaults?.days ?? 10,
  };

  // Align with saved cart dates so weekend days are not treated as unavailable
  // when validating and replacing initialDates (edit-from-cart flow).
  const inferredSaturdays = haveSaturdays(defaults?.dates);
  const inferredSundays = haveSundays(defaults?.dates);

  const { nextPossibleDays, lastPossibleOrderDay, areDatesAvailable } =
    useDietDates({
      diet,
      saturdays: inferredSaturdays,
      sundays: inferredSundays,
      locale,
    });

  const nextDays = nextPossibleDays(new Date(), defaultConfiguration.days);

  const baseVariant = diet?.base?.variant;
  const baseKcal = diet?.base?.caloricity?.toString();

  // variant selection
  let variant: DietVariant | null = null;

  if (defaults) {
    variant =
      getVariantFromMealsAndCalories(
        diet,
        defaults?.mealIds,
        defaults?.calories,
      ) ?? null;
  }

  const originalDates: Date[] | undefined = defaults?.dates?.map(
    (date) => new Date(date),
  );

  // Some of the saved delivery days may no longer be available (edit-from-cart
  // flow): drop them and keep the rest of the user's choice untouched. No
  // replacement is picked and the calendar never starts over from the first
  // available day; `daysToSelect` below follows the shorter selection.
  const initialDates =
    originalDates && !areDatesAvailable(originalDates)
      ? dropUnavailableDates({
          dates: originalDates,
          isDateAvailable: (date) => areDatesAvailable([date]),
        })
      : (originalDates ?? nextDays);

  // if there are any configuration from the initial state
  // for example - query params from listing or during editing the configuration (not adding to cart)
  const notEmpty = Boolean(defaults && !isEmpty(defaults));

  return useFormResolver({
    schema: RefinedSchema({
      min,
      max,
      minMeals: minMeals ?? 1,
      lastPossibleOrderDay,
    }),
    defaultValues: notEmpty
      ? {
          dates: initialDates,
          kcal:
            defaults?.sourceCalorifics?.toString() ??
            (defaults?.calories && defaults?.calories < +(baseKcal ?? 0)
              ? baseKcal
              : defaults?.calories?.toString()) ??
            baseKcal ??
            "",

          meals: variant
            ? (variant.mealTypes?.map((mt) => mt.id) ?? [])
            : (defaults?.mealIds ?? []),
          daysToSelect: initialDates.length || defaultConfiguration.days,
          saturdays: haveSaturdays(defaults?.dates),
          sundays: haveSundays(defaults?.dates),
          variant: variant?.id || baseVariant || "",
        }
      : {
          dates: nextDays,
          kcal: baseKcal,
          meals:
            variant?.mealTypes?.map((mt) => mt.id) ?? defaults?.mealIds ?? [],
          daysToSelect: defaultConfiguration.days,
          saturdays: defaultConfiguration.saturdays,
          sundays: defaultConfiguration.sundays,
          variant: baseVariant || "",
        },
  });
};

export type ConfigureDietFormType = z.output<ReturnType<typeof RefinedSchema>>;
export type ConfigureDietReturnFormType = UseFormReturn<
  ConfigureDietFormType,
  unknown,
  ConfigureDietFormType
>;
